import React, { useEffect, useState } from "react";
import { Box, IconButton, useMediaQuery } from "@mui/material";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import TrendingDownOutlinedIcon from "@mui/icons-material/TrendingDownOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { dt } from "../../../../shared/themes/redesignTokens";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { ImageGallery, useGalleryImages } from "../../../../shared/components/gallery";
import { ServiceCompletionDetails } from "../../models/executionModels";
import serviceExecutionApi from "../../api/serviceExecutionApi";
import dayjs from "dayjs";
import { formatDateTimeAt } from "../../../../shared/utils/formatDateTime";
import { getUiLocale } from "../../../../shared/utils/relativeTime";

// ===================================================================
//  Props
// ===================================================================
interface RfsCompletionCardProps {
  completion: ServiceCompletionDetails;
  serviceRequestId: string;
  files?: Array<{ name: string; size: number }>;
  /** When true: no border, no shadow, no animation, transparent bg — used inside logbook milestone */
  embedded?: boolean;
}

// ===================================================================
//  Sub-components
// ===================================================================

/** Cost comparison row with delta coloring */
const CostCompareRow = ({
  label,
  actual,
  estimated,
  isBold,
  formatCurrency,
}: {
  label: string;
  actual?: number | null;
  estimated?: number | null;
  isBold?: boolean;
  formatCurrency: (val?: number | null) => string;
}) => {
  const actualStr = formatCurrency(actual);
  const estimatedStr = formatCurrency(estimated);

  // Delta analysis
  let deltaColor = dt.textMuted;
  let deltaIcon: React.ReactNode = null;
  if (actual != null && estimated != null && estimated > 0) {
    const diff = actual - estimated;
    const pct = Math.round((diff / estimated) * 100);
    if (diff < 0) {
      deltaColor = "#059669"; // Under budget — green
      deltaIcon = (
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}>
          <TrendingDownOutlinedIcon sx={{ fontSize: 14, color: deltaColor }} />
          <Box component="span" sx={{ fontSize: "11px", fontFamily: "var(--font-mono)", fontWeight: 600, color: deltaColor }}>
            {pct}%
          </Box>
        </Box>
      );
    } else if (diff > 0) {
      deltaColor = "#DC2626"; // Over budget — red
      deltaIcon = (
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}>
          <TrendingUpOutlinedIcon sx={{ fontSize: 14, color: deltaColor }} />
          <Box component="span" sx={{ fontSize: "11px", fontFamily: "var(--font-mono)", fontWeight: 600, color: deltaColor }}>
            +{pct}%
          </Box>
        </Box>
      );
    }
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr auto auto auto",
        alignItems: "center",
        gap: { xs: 1, sm: 2 },
        py: 1,
        px: isBold ? 1.5 : 0,
        borderRadius: isBold ? dt.radiusSm : 0,
        backgroundColor: isBold ? "rgba(39, 174, 96, 0.05)" : "transparent",
      }}
    >
      {/* Label */}
      <Box
        sx={{
          fontSize: "13px",
          fontWeight: isBold ? 700 : 500,
          fontFamily: "var(--font-sans)",
          color: dt.textPrimary,
        }}
      >
        {label}
      </Box>

      {/* Actual */}
      <Box
        sx={{
          fontSize: "13px",
          fontWeight: isBold ? 700 : 600,
          fontFamily: "var(--font-mono)",
          color: "#059669",
          textAlign: "right",
          whiteSpace: "nowrap",
        }}
      >
        {actualStr}
      </Box>

      {/* Estimated */}
      <Box
        sx={{
          fontSize: "12px",
          fontWeight: 400,
          fontFamily: "var(--font-mono)",
          color: dt.textMuted,
          textAlign: "right",
          whiteSpace: "nowrap",
          textDecoration: actual != null ? "line-through" : "none",
          opacity: actual != null ? 0.7 : 1,
        }}
      >
        {estimatedStr}
      </Box>

      {/* Delta */}
      <Box sx={{ minWidth: "50px", textAlign: "right" }}>{deltaIcon}</Box>
    </Box>
  );
};

// ===================================================================
//  Main Component
// ===================================================================
// Image extension check (same pattern as ViewServiceRequestAttachmentsCard)
const IMAGE_EXTENSIONS = /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i;
const isImageFile = (file: { name: string; content?: string }): boolean => {
  return IMAGE_EXTENSIONS.test(file.name) || (file.content?.startsWith("data:image/") ?? false);
};

const RfsCompletionCard: React.FC<RfsCompletionCardProps> = ({ completion, serviceRequestId, files, embedded }) => {
  const { translate } = useTranslation(["servicing", "common"]);
  const isPhone = useMediaQuery("(max-width:768px)");

  // Fetch full file data (with content/URLs) from the API
  const [fullFiles, setFullFiles] = useState<Array<{ name: string; content: string; size: number }>>([]);
  const [filesLoading, setFilesLoading] = useState(false);

  useEffect(() => {
    if (!serviceRequestId || (!files || files.length === 0)) return;
    setFilesLoading(true);
    serviceExecutionApi
      .getCompletionFiles(serviceRequestId)
      .then((data) => setFullFiles(data))
      .catch(() => setFullFiles([]))
      .finally(() => setFilesLoading(false));
  }, [serviceRequestId, files]);

  // Split into images vs non-images
  const imageFiles = fullFiles.filter(isImageFile);
  const nonImageFiles = fullFiles.filter((f) => !isImageFile(f));
  const galleryImages = useGalleryImages({ type: "entityFiles", files: imageFiles });

  // File count from prop (before API data loads) vs full files
  const hasFiles = (files && files.length > 0) || fullFiles.length > 0;

  const handleDownload = (file: { name: string; content: string }) => {
    const link = document.createElement("a");
    link.href = file.content;
    link.download = file.name;
    link.target = "_blank";
    link.click();
    link.remove();
  };

  const formatCurrency = (val?: number | null): string => {
    if (val == null) return "\u2014";
    return `EUR ${val.toLocaleString(getUiLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return "\u2014";
    return formatDateTimeAt(dateStr);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const hasCostComparison =
    completion.actualLaborHours != null ||
    completion.actualPartsCost != null ||
    completion.finalCost != null;

  return (
    <Box
      sx={{
        borderRadius: embedded ? dt.radiusSm : dt.radius,
        border: embedded ? "none" : `1px solid ${dt.cardBorder}`,
        borderLeft: embedded ? "none" : "4px solid #10B981",
        backgroundColor: embedded ? "transparent" : dt.cardBg,
        overflow: "hidden",
        boxShadow: embedded ? "none" : dt.cardShadow,
        animation: embedded ? "none" : "sectionReveal 0.4s ease-out both",
      }}
    >
      {/* Top accent bar — emerald gradient (hidden in embedded mode) */}
      {!embedded && (
        <Box
          sx={{
            height: "3px",
            background: "linear-gradient(90deg, #10B981 0%, #34D399 50%, transparent 100%)",
          }}
        />
      )}

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: isPhone ? 2 : 2.5,
          pt: 2,
          pb: 1,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            backgroundColor: "rgba(16, 185, 129, 0.10)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CheckCircleOutlinedIcon sx={{ color: "#10B981", fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              fontSize: isPhone ? "15px" : "16px",
              fontWeight: 700,
              fontFamily: "var(--font-sans)",
              color: dt.textPrimary,
              letterSpacing: "-0.2px",
            }}
          >
            {translate("servicing:completion.reportTitle")}
          </Box>
          <Box
            sx={{
              fontSize: "12px",
              fontFamily: "var(--font-sans)",
              fontWeight: 500,
              color: "#10B981",
            }}
          >
            {completion.providerName}
          </Box>
        </Box>
      </Box>

      {/* Content body */}
      <Box sx={{ px: isPhone ? 2 : 2.5, pb: 2.5 }}>
        {/* Work Summary */}
        <SectionLabel label={translate("servicing:completion.summary")} />
        <Box
          sx={{
            fontSize: "13px",
            fontFamily: "var(--font-sans)",
            fontWeight: 400,
            lineHeight: 1.7,
            color: "#374151",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            mb: 2,
          }}
        >
          {completion.completionSummary}
        </Box>

        {/* Cost Comparison */}
        {hasCostComparison && (
          <>
            <SectionLabel label={translate("servicing:completion.costComparison")} />
            <Box
              sx={{
                borderRadius: dt.radiusSm,
                border: `1px solid ${dt.rowBorder}`,
                backgroundColor: "#FAFBFC",
                p: isPhone ? 1.5 : 2,
                mb: 2,
              }}
            >
              {/* Column headers */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto auto auto",
                  gap: { xs: 1, sm: 2 },
                  pb: 0.75,
                  mb: 0.5,
                  borderBottom: `1px solid ${dt.rowBorder}`,
                }}
              >
                <Box sx={{ fontSize: "10px", fontWeight: 700, fontFamily: "var(--font-sans)", textTransform: "uppercase", letterSpacing: "0.8px", color: dt.textMuted }} />
                <Box sx={{ fontSize: "10px", fontWeight: 700, fontFamily: "var(--font-sans)", textTransform: "uppercase", letterSpacing: "0.8px", color: "#059669", textAlign: "right" }}>
                  {translate("servicing:completion.actual")}
                </Box>
                <Box sx={{ fontSize: "10px", fontWeight: 700, fontFamily: "var(--font-sans)", textTransform: "uppercase", letterSpacing: "0.8px", color: dt.textMuted, textAlign: "right" }}>
                  {translate("servicing:completion.estimatedLabel")}
                </Box>
                <Box sx={{ minWidth: "50px" }} />
              </Box>

              {/* Rows */}
              {(completion.actualLaborHours != null || completion.estimatedLaborHours != null) && (
                <CostCompareRow
                  label={translate("servicing:completion.laborHours")}
                  actual={completion.actualLaborHours}
                  estimated={completion.estimatedLaborHours}
                  formatCurrency={(v) => (v != null ? `${v}h` : "\u2014")}
                />
              )}
              {(completion.actualPartsCost != null || completion.estimatedPartsCost != null) && (
                <CostCompareRow
                  label={translate("servicing:completion.partsCost")}
                  actual={completion.actualPartsCost}
                  estimated={completion.estimatedPartsCost}
                  formatCurrency={formatCurrency}
                />
              )}
              <Box sx={{ borderTop: `1px solid ${dt.rowBorder}`, mt: 0.5, pt: 0.5 }}>
                <CostCompareRow
                  label={translate("servicing:completion.totalCost")}
                  actual={completion.finalCost}
                  estimated={completion.estimatedTotalCost}
                  isBold
                  formatCurrency={formatCurrency}
                />
              </Box>
            </Box>
          </>
        )}

        {/* Provider Notes */}
        {completion.providerNotes && (
          <>
            <SectionLabel label={translate("servicing:completion.providerNotes")} />
            <Box
              sx={{
                fontSize: "13px",
                fontFamily: "var(--font-sans)",
                fontWeight: 400,
                lineHeight: 1.6,
                color: "#374151",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                backgroundColor: dt.inputBg,
                borderRadius: dt.radiusSm,
                px: 2,
                py: 1.5,
                mb: 2,
              }}
            >
              {completion.providerNotes}
            </Box>
          </>
        )}

        {/* Revision warning (if previously revised) */}
        {completion.revisionRequestedDate && (
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              backgroundColor: "rgba(245, 158, 11, 0.06)",
              borderLeft: "3px solid #F59E0B",
              borderRadius: `0 ${dt.radiusSm} ${dt.radiusSm} 0`,
              p: 1.5,
              mb: 2,
            }}
          >
            <WarningAmberOutlinedIcon sx={{ color: "#F59E0B", fontSize: 20, mt: "1px" }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  fontSize: "12px",
                  fontWeight: 700,
                  fontFamily: "var(--font-sans)",
                  color: "#92400E",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  mb: 0.5,
                }}
              >
                {translate("servicing:completion.previousRevision")}
              </Box>
              <Box
                sx={{
                  fontSize: "13px",
                  fontFamily: "var(--font-sans)",
                  fontWeight: 400,
                  color: "#78350F",
                  lineHeight: 1.5,
                  mb: 0.5,
                }}
              >
                {completion.revisionReason}
              </Box>
              <Box
                sx={{
                  fontSize: "11px",
                  fontFamily: "var(--font-mono)",
                  fontWeight: 500,
                  color: "#B45309",
                  letterSpacing: "0.3px",
                }}
              >
                {formatDate(completion.revisionRequestedDate)}
              </Box>
            </Box>
          </Box>
        )}

        {/* Attached files — split into images (gallery) + documents (download rows) */}
        {hasFiles && (
          <>
            {/* Image gallery */}
            {galleryImages.length > 0 && (
              <>
                <SectionLabel label={translate("servicing:completion.workPhotos")} />
                <Box sx={{ mb: 2 }}>
                  <ImageGallery
                    images={galleryImages}
                    variant="detail"
                    showThumbnails
                  />
                </Box>
              </>
            )}

            {/* Non-image files — downloadable rows */}
            {nonImageFiles.length > 0 && (
              <>
                <SectionLabel label={translate("servicing:completion.documents")} />
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, mb: 2 }}>
                  {nonImageFiles.map((file, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 1.5,
                        py: 1,
                        borderRadius: dt.radiusSm,
                        backgroundColor: dt.inputBg,
                        border: `1px solid ${dt.cardBorder}`,
                        transition: "background-color 0.15s ease",
                        "&:hover": { backgroundColor: "#EEF2F7" },
                      }}
                    >
                      <InsertDriveFileOutlinedIcon sx={{ fontSize: 16, color: dt.textMuted }} />
                      <Box
                        sx={{
                          fontSize: "12px",
                          fontFamily: "var(--font-sans)",
                          fontWeight: 500,
                          color: dt.textPrimary,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        {file.name}
                      </Box>
                      <Box
                        sx={{
                          fontSize: "11px",
                          fontFamily: "var(--font-mono)",
                          fontWeight: 500,
                          color: dt.textMuted,
                          flexShrink: 0,
                        }}
                      >
                        {formatFileSize(file.size)}
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => handleDownload(file)}
                        sx={{
                          p: 0.5,
                          color: dt.textMuted,
                          transition: "color 0.15s ease",
                          "&:hover": { color: dt.textPrimary },
                        }}
                      >
                        <DownloadOutlinedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              </>
            )}

            {/* Loading state — show basic file list from prop while API loads */}
            {filesLoading && fullFiles.length === 0 && files && files.length > 0 && (
              <>
                <SectionLabel label={translate("servicing:completion.attachments")} />
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, mb: 2 }}>
                  {files.map((file, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 1.5,
                        py: 1,
                        borderRadius: dt.radiusSm,
                        backgroundColor: dt.inputBg,
                        border: `1px solid ${dt.cardBorder}`,
                        opacity: 0.6,
                      }}
                    >
                      <InsertDriveFileOutlinedIcon sx={{ fontSize: 16, color: dt.textMuted }} />
                      <Box
                        sx={{
                          fontSize: "12px",
                          fontFamily: "var(--font-sans)",
                          fontWeight: 500,
                          color: dt.textPrimary,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        {file.name}
                      </Box>
                      <Box
                        sx={{
                          fontSize: "11px",
                          fontFamily: "var(--font-mono)",
                          fontWeight: 500,
                          color: dt.textMuted,
                          flexShrink: 0,
                        }}
                      >
                        {formatFileSize(file.size)}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </>
        )}

        {/* Completion dates footer */}
        <Box
          sx={{
            display: "flex",
            flexDirection: isPhone ? "column" : "row",
            gap: isPhone ? 0.75 : 3,
            pt: 1.5,
            borderTop: `1px solid ${dt.rowBorder}`,
          }}
        >
          <DateRow
            icon={<EventOutlinedIcon sx={{ fontSize: 14 }} />}
            label={translate("servicing:completion.completedOn")}
            date={formatDate(completion.completedByProviderDate)}
          />
          {completion.confirmedByOwnerDate && (
            <DateRow
              icon={<CheckCircleOutlinedIcon sx={{ fontSize: 14 }} />}
              label={translate("servicing:completion.confirmedOn")}
              date={formatDate(completion.confirmedByOwnerDate)}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

// ===================================================================
//  Small sub-components
// ===================================================================

const SectionLabel = ({ label }: { label: string }) => (
  <Box
    sx={{
      fontSize: "11px",
      fontWeight: 700,
      fontFamily: "var(--font-sans)",
      textTransform: "uppercase",
      letterSpacing: "1px",
      color: dt.textMuted,
      mb: "8px",
    }}
  >
    {label}
  </Box>
);

const DateRow = ({
  icon,
  label,
  date,
}: {
  icon: React.ReactNode;
  label: string;
  date: string;
}) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
    <Box sx={{ color: dt.textMuted, display: "flex" }}>{icon}</Box>
    <Box
      sx={{
        fontSize: "11px",
        fontFamily: "var(--font-sans)",
        fontWeight: 500,
        color: dt.textSecondary,
      }}
    >
      {label}:
    </Box>
    <Box
      sx={{
        fontSize: "11px",
        fontFamily: "var(--font-mono)",
        fontWeight: 600,
        color: dt.textPrimary,
        letterSpacing: "0.3px",
      }}
    >
      {date}
    </Box>
  </Box>
);

export default RfsCompletionCard;
